import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Users, Plus, LogIn, Crown, Share2, Copy, Send, MessageSquare } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import Podium from "@/components/Podium";

const QUICK_REACTIONS = ["🔥", "😂", "🏆", "🍋", "👏"];

function LeagueWall({ code }) {
  const qc = useQueryClient();
  const [text, setText] = useState("");
  const { data: messages = [] } = useQuery({
    queryKey: ["league-msgs", code],
    queryFn: async () => (await api.get(`/leagues/${code}/messages`)).data,
    refetchInterval: 10000,
  });

  const send = async (msgText) => {
    const t = (msgText ?? text).trim();
    if (!t) return;
    try {
      await api.post(`/leagues/${code}/messages`, { text: t });
      setText("");
      qc.invalidateQueries({ queryKey: ["league-msgs", code] });
    } catch (e) {
      toast.error(errMsg(e, "Invio fallito"));
    }
  };

  return (
    <div data-testid="league-wall" className="rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] overflow-hidden">
      <div className="px-5 py-4 border-b border-[#3F3F4C] flex items-center gap-2">
        <MessageSquare className="w-4 h-4 text-[#FF1E27]" />
        <h4 className="font-racing font-bold text-white text-xs uppercase tracking-wide">
          Bacheca / Wall della Lega
        </h4>
      </div>
      <div className="max-h-72 overflow-y-auto px-4 py-3 space-y-2.5">
        {messages.length === 0 && (
          <p className="text-xs text-gray-500 text-center py-6">
            Nessun messaggio. Rompi il ghiaccio e sfida i tuoi avversari!
          </p>
        )}
        {messages.map((m) =>
          m.kind === "system" ? (
            <p key={m.id} className="text-center text-[11px] font-body-sport italic text-[#FFB703] py-1" data-testid={`wall-system-${m.id}`}>
              ⚑ {m.text}
            </p>
          ) : (
            <div key={m.id} className="flex flex-col" data-testid={`wall-msg-${m.id}`}>
              <span className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
                {m.username}
              </span>
              <span className="text-sm text-gray-200 bg-[#242429] rounded-lg rounded-tl-none px-3 py-1.5 mt-0.5 self-start max-w-[90%] break-words">
                {m.text}
              </span>
            </div>
          )
        )}
      </div>
      <div className="border-t border-[#3F3F4C] px-3 py-2.5 space-y-2">
        <div className="flex gap-1.5">
          {QUICK_REACTIONS.map((r) => (
            <button
              key={r}
              data-testid={`wall-reaction-${r}`}
              onClick={() => send(r)}
              className="text-lg px-2 py-1 min-h-[40px] rounded-lg hover:bg-[#242429] transition-colors"
            >
              {r}
            </button>
          ))}
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            send();
          }}
          className="flex gap-2"
        >
          <input
            data-testid="wall-message-input"
            className="flex-1 min-w-0 rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport"
            placeholder="Scrivi un messaggio (max 280 caratteri)..."
            maxLength={280}
            value={text}
            onChange={(e) => setText(e.target.value)}
          />
          <button
            data-testid="wall-send-button"
            className="shrink-0 flex items-center justify-center rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 min-h-[40px] text-white"
          >
            <Send className="w-4 h-4" />
          </button>
        </form>
      </div>
    </div>
  );
}

function ShareInvite({ code, name }) {
  const url = `${window.location.origin}/join-league?code=${code}`;
  const text = `Unisciti alla mia lega "${name}" su FantasyRace 69° Salita dei Monti Iblei! Codice: ${code}`;

  const nativeShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: "FantasyRace — Invito Lega", text, url });
      } catch {
        // condivisione annullata o non supportata
      }
    } else {
      await navigator.clipboard.writeText(url);
      toast.success("Link di invito copiato!");
    }
  };

  const itemCls =
    "w-full flex items-center gap-2 rounded-lg px-3 py-2.5 min-h-[44px] text-xs font-body-sport font-bold text-gray-300 hover:bg-[#242429] hover:text-white transition-colors text-left";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={`share-invite-${code}`}
          className="flex items-center gap-2 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 py-2 min-h-[40px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
        >
          <Share2 className="w-4 h-4" /> Invita Amici
        </button>
      </PopoverTrigger>
      <PopoverContent className="bg-[#1A1A1E] border-[#3F3F4C] w-60 p-2" data-testid="share-menu">
        <button onClick={nativeShare} className={itemCls} data-testid="share-native">
          <Share2 className="w-4 h-4 text-[#FF1E27]" /> Condividi...
        </button>
        <button
          onClick={async () => {
            await navigator.clipboard.writeText(url);
            toast.success("Link di invito copiato!");
          }}
          className={itemCls}
          data-testid="share-copy"
        >
          <Copy className="w-4 h-4 text-[#FFB703]" /> Copia Link
        </button>
        <a
          href={`https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={itemCls}
          data-testid="share-whatsapp"
        >
          <span className="w-4 h-4 rounded-full bg-[#22C55E] inline-block" /> WhatsApp
        </a>
        <a
          href={`https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={itemCls}
          data-testid="share-telegram"
        >
          <span className="w-4 h-4 rounded-full bg-[#3B82F6] inline-block" /> Telegram
        </a>
      </PopoverContent>
    </Popover>
  );
}

function MaxEditor({ detail, onSaved }) {
  const [v, setV] = useState(detail.max_members ?? 20);
  return (
    <div className="flex items-center gap-1.5" title="Limite partecipanti (solo creatore)">
      <input
        type="number"
        min="2"
        max="200"
        data-testid="league-max-input"
        className="w-16 rounded-lg bg-[#121214] border border-[#3F3F4C] px-2 py-1.5 font-time text-xs text-white focus:outline-none focus:border-[#FFB703]"
        value={v}
        onChange={(e) => setV(e.target.value)}
      />
      <button
        data-testid="league-max-save"
        onClick={async () => {
          try {
            await api.put(`/leagues/${detail.code}`, { max_members: parseInt(v) });
            toast.success("Limite partecipanti aggiornato");
            onSaved();
          } catch (e) {
            toast.error(errMsg(e, "Aggiornamento fallito"));
          }
        }}
        className="rounded-lg border border-[#3F3F4C] px-2.5 py-1.5 min-h-[32px] text-[10px] font-body-sport font-bold text-gray-300 hover:border-[#FFB703] hover:text-[#FFB703] transition-colors uppercase"
      >
        Salva limite
      </button>
    </div>
  );
}

export default function Leagues() {
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const [name, setName] = useState("");
  const [maxMembers, setMaxMembers] = useState(20);
  const [code, setCode] = useState("");
  const [selected, setSelected] = useState(null);
  const joinAttempted = useRef(false);

  const { data: leagues = [] } = useQuery({
    queryKey: ["my-leagues"],
    queryFn: async () => (await api.get("/leagues/mine")).data,
  });

  const { data: detail } = useQuery({
    queryKey: ["league", selected],
    queryFn: async () => (await api.get(`/leagues/${selected}`)).data,
    enabled: !!selected,
    refetchInterval: 10000,
  });

  const doJoin = async (rawCode) => {
    try {
      const { data } = await api.post("/leagues/join", { code: rawCode });
      toast.success(
        data.already ? `Sei già nella lega "${data.league.name}"` : `Benvenuto nella lega "${data.league.name}"!`
      );
      qc.invalidateQueries({ queryKey: ["my-leagues"] });
      setSelected(data.league.code);
    } catch (e) {
      toast.error(errMsg(e, "Codice non valido"));
    }
  };

  useEffect(() => {
    const c = params.get("code");
    if (c && !joinAttempted.current) {
      joinAttempted.current = true;
      doJoin(c);
      setParams({}, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const createLeague = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/leagues", { name, max_members: parseInt(maxMembers) || 20 });
      toast.success(`Lega "${data.name}" creata! Codice: ${data.code}`);
      setName("");
      setMaxMembers(20);
      qc.invalidateQueries({ queryKey: ["my-leagues"] });
      setSelected(data.code);
    } catch (e) {
      toast.error(errMsg(e, "Creazione fallita"));
    }
  };

  const copyInvite = (c) => {
    const url = `${window.location.origin}/leghe?code=${c}`;
    navigator.clipboard.writeText(url);
    toast.success("Link di invito copiato!");
  };

  const inputCls =
    "rounded-lg bg-[#121214] border border-[#3F3F4C] px-3 py-2 text-sm text-white placeholder:text-gray-600 focus:outline-none focus:border-[#FF1E27] transition-colors font-body-sport";

  return (
    <div data-testid="leagues-page" className="space-y-8">
      <h2 className="font-racing font-bold text-xl sm:text-2xl uppercase tracking-wide text-white border-l-4 border-[#FF1E27] pl-3">
        Le Mie Leghe
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <form
          onSubmit={createLeague}
          className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3"
        >
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
            <Plus className="w-4 h-4 text-[#FF1E27]" /> Crea Lega Privata
          </h3>
          <div className="flex gap-2">
            <input
              data-testid="create-league-input"
              className={`${inputCls} flex-1 min-w-0`}
              placeholder="Nome della lega"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
            <button
              data-testid="create-league-button"
              className="shrink-0 rounded-lg bg-[#FF1E27] hover:bg-[#CC1118] transition-colors px-4 min-h-[40px] font-body-sport font-bold text-xs tracking-widest text-white uppercase"
            >
              Crea
            </button>
          </div>
          <div className="flex items-center gap-2">
            <label className="font-body-sport text-[10px] font-bold tracking-widest text-gray-500 uppercase">
              Limite partecipanti
            </label>
            <input
              type="number"
              min="2"
              max="200"
              data-testid="create-league-max-input"
              className={`${inputCls} w-20`}
              value={maxMembers}
              onChange={(e) => setMaxMembers(e.target.value)}
            />
          </div>
        </form>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            doJoin(code);
            setCode("");
          }}
          className="rounded-xl border border-[#3F3F4C] bg-[#2A2A32] p-5 space-y-3"
        >
          <h3 className="font-body-sport font-bold text-white text-sm uppercase tracking-widest flex items-center gap-2">
            <LogIn className="w-4 h-4 text-[#FFB703]" /> Unisciti a una Lega
          </h3>
          <div className="flex gap-2">
            <input
              data-testid="join-league-input"
              className={`${inputCls} flex-1`}
              placeholder="Codice invito (es. X7Y9Z2)"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
            />
            <button
              data-testid="join-league-button"
              className="rounded-lg border border-[#FFB703] text-[#FFB703] hover:bg-[#FFB703]/10 transition-colors px-4 font-body-sport font-bold text-xs tracking-widest uppercase"
            >
              Entra
            </button>
          </div>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="space-y-3">
          <div className="rounded-xl border border-[#FFB703]/40 bg-[#FFB703]/5 p-4">
            <p className="font-body-sport text-[10px] font-bold tracking-widest text-[#FFB703] uppercase">
              Lega Pubblica Globale
            </p>
            <p className="text-xs text-gray-400 mt-1">
              Sei iscritto automaticamente alla Classifica Generale 69° Salita dei Monti Iblei.
            </p>
          </div>
          {leagues.map((l) => (
            <button
              key={l.id}
              data-testid={`league-item-${l.code}`}
              onClick={() => setSelected(l.code)}
              className={`w-full text-left rounded-xl border p-4 transition-colors ${
                selected === l.code
                  ? "border-[#FF1E27] bg-[#FF1E27]/5"
                  : "border-[#3F3F4C] bg-[#2A2A32] hover:border-[#FF1E27]/50"
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <p className="font-body-sport font-bold text-white text-sm flex items-center gap-2 truncate">
                  <Users className="w-4 h-4 text-[#FF1E27] shrink-0" />
                  {l.name}
                  {l.is_owner && <Crown className="w-3.5 h-3.5 text-[#FFB703] shrink-0" />}
                </p>
                <span className="font-time text-[10px] text-gray-500 shrink-0">
                  {l.members_count}/{l.max_members ?? 20} membri
                </span>
              </div>
              <div className="flex items-center justify-between mt-2">
                <span className="font-time text-xs text-[#FFB703] tracking-widest">{l.code}</span>
                <span
                  data-testid={`copy-invite-${l.code}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    copyInvite(l.code);
                  }}
                  className="flex items-center gap-1 text-[10px] font-body-sport text-gray-400 hover:text-white transition-colors cursor-pointer"
                >
                  <Copy className="w-3 h-3" /> Copia invito
                </span>
              </div>
            </button>
          ))}
          {leagues.length === 0 && (
            <p className="text-xs text-gray-500 px-1">
              Non fai ancora parte di nessuna lega privata.
            </p>
          )}
        </div>

        <div className="lg:col-span-2">
          {detail ? (
            <>
            <div className="rounded-xl border border-[#3F3F4C] bg-[#1A1A1E] overflow-hidden">
              <div className="px-5 py-4 border-b border-[#3F3F4C] flex flex-wrap items-center justify-between gap-3">
                <div>
                  <h3 className="font-racing font-bold text-white text-sm uppercase tracking-wide">
                    {detail.name}
                  </h3>
                  <p className="font-time text-[10px] text-gray-500 mt-0.5">
                    {detail.code} • {detail.members.length}/{detail.max_members ?? 20} partecipanti
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  {detail.is_owner && (
                    <MaxEditor
                      detail={detail}
                      onSaved={() => {
                        qc.invalidateQueries({ queryKey: ["league", detail.code] });
                        qc.invalidateQueries({ queryKey: ["my-leagues"] });
                      }}
                    />
                  )}
                  <ShareInvite code={detail.code} name={detail.name} />
                </div>
              </div>
              <Podium
                items={detail.members.slice(0, 3).map((m) => ({ pos: m.posizione, title: m.username, points: m.total_points }))}
                testidPrefix="league-podium"
              />
              {detail.members.map((m, i) => (
                <div
                  key={m.username}
                  data-testid={`league-member-${m.username}`}
                  className={`grid grid-cols-[3rem_1fr_6rem] items-center px-5 py-3 border-b border-[#242429] ${
                    i % 2 === 0 ? "bg-[#1A1A1E]" : "bg-[#141416]"
                  } ${m.is_me ? "border-l-2 border-[#FF1E27]" : ""}`}
                >
                  <span
                    className={`font-racing font-black ${
                      ({ 1: "text-[#FFD700]", 2: "text-[#C0C0C0]", 3: "text-[#CD7F32]" })[m.posizione] || "text-gray-500"
                    }`}
                  >
                    {m.posizione}
                  </span>
                  <span className="font-body-sport font-semibold text-white text-sm truncate">
                    {m.username}
                    <span className="block text-[10px] text-gray-500 font-normal">{m.team_name}</span>
                  </span>
                  <span className="font-time font-bold text-[#FFB703] text-right">{m.total_points}</span>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <LeagueWall code={detail.code} />
            </div>
            </>
          ) : (
            <div className="rounded-xl border border-dashed border-[#3F3F4C] bg-[#1A1A1E]/50 p-12 text-center">
              <Users className="w-8 h-8 text-gray-600 mx-auto mb-3" />
              <p className="text-gray-500 text-sm">
                Seleziona una lega per vedere la classifica dedicata.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
